import { Component, EventEmitter, Output, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { NgIf, CommonModule } from '@angular/common';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { RfqIntakeService } from '../../../../services/rfq.intake.service';
import { IndexedDBService } from '../../../../services/indexed.db.service';
import { ActivatedRoute } from '@angular/router';
import { PhoneFormatDirective } from '../../../shared/directives/phone-format.directive';

@Component({
  selector: 'app-rfq-details',
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MultiSelectModule,
    SelectModule,
    CommonModule,
    DatePickerModule,
    PhoneFormatDirective
  ],
  templateUrl: './rfq-details.component.html',
  styleUrls: ['./rfq-details.component.scss']
})
export class RfqDetailsComponent implements OnInit {
  @Output() next = new EventEmitter<any>();
  @Input() showSummary: boolean = false;
  @Input() hasUploadedFiles: boolean = false;
  @Input() supplierCompanyName: string | null = null;
  certificateList: any[] = [];
  industryList: any[] = [];
  inspectionList: any[] = [];
  paymentTermsList: any[] = [];
  rfqForm: FormGroup;
  submitted = false;
  private rfqId: string;
  countryList: any[] = [];
  stateList: any[] = [];
  zipMaxLength:number = 10;
  private savedState: any = null;

  // Properties for cascading date pickers
  minQuoteByDate!: Date;
  minDeliverByDate: Date | null = null; // DeliverBy minDate, null if quoteBy is not set

  // Custom email validator
  private emailValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    
    // RFC 5322 compliant email regex
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    
    return emailRegex.test(control.value) ? null : { invalidEmail: true };
  }

  constructor(
    private fb: FormBuilder, 
    private rfqIntakeService: RfqIntakeService,
    private indexedDBService: IndexedDBService,
    private route: ActivatedRoute
  ) {
    this.rfqForm = this.fb.group({
      rfqName: ['', [Validators.required, Validators.pattern(/^(?!\s*$).+/)]],
      fullName: ['', [Validators.required, Validators.pattern(/^[a-zA-Z]+(?: [a-zA-Z]+)*$/)]],
      companyName: ['', [Validators.required, Validators.pattern(/^(?!\s*$).+/)]],
      email: ['', [Validators.required, this.emailValidator.bind(this)]],
      phoneNo: ['', [Validators.required, Validators.pattern(/^\+?[\d\s-]{10,15}$/)]],
      quoteBy: [null, Validators.required],
      deliverBy: ['', Validators.required],
      address1: ['', [Validators.required, Validators.pattern(/^(?!\s*$).+/)]],
      address2: [''],
      country: ['', Validators.required],
      state: ['', Validators.required],
      city: ['', [Validators.required, Validators.pattern(/^(?!\s*$).+/)]],
      zip: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9-]{2,4}\s?[A-Za-z0-9-]{2,4}$|^[0-9]{6}$|^[0-9]{5}-[0-9]{3,4}$/)]],
      certificates: [[]],
      industry: [[], Validators.required],
      inspection: [[]],
      paymentTerms: [[], Validators.required]
    });
    this.rfqId = this.route.snapshot.params['rfqId'] || 'temp-rfq';
  }

  get f() { return this.rfqForm.controls; }
  ngOnInit() {
    this.getCountryList();
    this.getRfqDetailDropdowns();
    this.loadSavedFormData();

    // Initialize minQuoteByDate to tomorrow
    this.minQuoteByDate = new Date();
    this.minQuoteByDate.setDate(this.minQuoteByDate.getDate() + 1); // Set to tomorrow
    this.minQuoteByDate.setHours(0, 0, 0, 0); // Clear time part

    // Subscribe to country changes
    this.rfqForm.get('country')?.valueChanges.subscribe(countryId => {
      if (countryId) {
        this.getStateList(countryId);
        // Reset state when country changes
        this.rfqForm.patchValue({ state: '' }, { emitEvent: false });
      }
    });

    // Subscribe to quoteBy date changes
    this.rfqForm.get('quoteBy')?.valueChanges.subscribe(selectedQuoteByDate => {
      if (selectedQuoteByDate) {
        // Ensure it's a Date object if coming from patchValue as string
        const quoteDate = new Date(selectedQuoteByDate);
        quoteDate.setHours(0,0,0,0); // Normalize

        // Set minDeliverByDate to the next day of quoteBy date
        this.minDeliverByDate = new Date(quoteDate);
        this.minDeliverByDate.setDate(quoteDate.getDate() + 1); // Set to next day

        this.rfqForm.get('deliverBy')?.enable(); // Enable deliverBy

        // If deliverBy date is already set and is now invalid, clear it
        const currentDeliverByDate = this.rfqForm.get('deliverBy')?.value;
        if (currentDeliverByDate) {
          const deliverDate = new Date(currentDeliverByDate);
          if (deliverDate <= quoteDate) { // Changed to ensure deliver date is after quote date
            this.rfqForm.get('deliverBy')?.setValue(null); // Clear invalid deliverBy date
          }
        }
      } else {
        // quoteBy date was cleared
        this.minDeliverByDate = null;
        this.rfqForm.get('deliverBy')?.setValue(null); // Clear deliverBy date
        this.rfqForm.get('deliverBy')?.disable(); // Disable deliverBy
      }
    });

    // Initial state for deliverBy: if quoteBy is not pre-filled, deliverBy should be disabled.
    if (!this.rfqForm.get('quoteBy')?.value) {
      this.rfqForm.get('deliverBy')?.disable();
    }
  }

  private loadSavedFormData() {
    this.indexedDBService.getRfqIntakeData(this.rfqId).subscribe({
      next: (data) => {
        if (data && data.rfqDetails) {
          // Patch the form with IDs
          this.rfqForm.patchValue({
            ...data.rfqDetails,
            country: data.rfqDetails.countryId || '', // use ID if available
            state: data.rfqDetails.stateId || ''
          });
          this.savedState = data.rfqDetails.stateId || '';
          if (data.rfqDetails.countryId) {
            this.getStateList(data.rfqDetails.countryId);
          }
        }
      },
      error: (error) => {
        console.error('Error loading saved form data:', error);
      }
    });
  }

  onSubmit() {
    this.submitted = true;
    if (this.rfqForm.invalid) {
      this.rfqForm.markAllAsTouched();
      return;
    }

    const formData = this.rfqForm.getRawValue();
    
    // Find the country label
    const selectedCountry = this.countryList.find(
      c => c.countryId === formData.country
    );
    const countryLabel = selectedCountry ? selectedCountry.country : '';

    // Find the state label
    const selectedState = this.stateList.find(
      s => s.stateId === formData.state
    );
    const stateLabel = selectedState ? selectedState.state : '';

    // Set current time for dates
    const now = new Date();
    const currentTime = {
      hours: now.getHours(),
      minutes: now.getMinutes(),
      seconds: now.getSeconds(),
      milliseconds: now.getMilliseconds()
    };

    // Update dates with current time
    const quoteByDate = formData.quoteBy ? new Date(formData.quoteBy) : null;
    if (quoteByDate) {
      quoteByDate.setHours(currentTime.hours, currentTime.minutes, currentTime.seconds, currentTime.milliseconds);
      formData.quoteBy = quoteByDate;
    }

    const deliverByDate = formData.deliverBy ? new Date(formData.deliverBy) : null;
    if (deliverByDate) {
      deliverByDate.setHours(currentTime.hours, currentTime.minutes, currentTime.seconds, currentTime.milliseconds);
      formData.deliverBy = deliverByDate;
    }

    // Build the object to save/send
    const dataToSave = {
      ...formData,
      country: countryLabel,
      countryId: formData.country,
      state: stateLabel,
      stateId: formData.state
    };

    // Using the new saveRfqIntakeData method
    this.indexedDBService.getRfqIntakeData(this.rfqId).subscribe({
      next: (existing: any) => {
        // Check if part details exist
        const hasPartDetails = existing?.partDetails && existing.partDetails.length > 0;

        const updated = {
          ...existing,
          rfqId: this.rfqId,
          rfqDetails: dataToSave,
          partDetails: existing?.partDetails || []
        };

        this.indexedDBService.saveRfqIntakeData(updated).subscribe({
          next: () => {
            // Set flags based on existing part details
            const flags = {
              showSummary: hasPartDetails,
              hasUploadedFiles: hasPartDetails
            };

            const emitData = {
              formData: dataToSave,
              ...flags
            };
            // Emit both the form data and flags to the second component
            this.next.emit(emitData);
          },
          error: (error) => {
            // On error, set both flags to false
            const flags = {
              showSummary: false,
              hasUploadedFiles: false
            };

            // Still emit the data with flags set to false
            this.next.emit({
              formData: dataToSave,
              ...flags
            });
          }
        });
      },
      error: (error) => {
        console.error('Error loading existing data:', error);
        // On error, set both flags to false
        const flags = {
          showSummary: false,
          hasUploadedFiles: false
        };

        // Still emit the data with flags set to false
        this.next.emit({
          formData: dataToSave,
          ...flags
        });
      }
    });
  }

  getRfqDetailDropdowns() {
    this.rfqIntakeService.getRfqDetailDropdowns().subscribe((res: any) => {
      if (res.status && res.data) {
        res.data.forEach((item: any) => {
          switch (item.masterKey) {
            case 'certificates':
              this.certificateList = item.masterValue;
              break;
            case 'industries':
              this.industryList = item.masterValue;
              break;
            case 'inspections':
              this.inspectionList = item.masterValue;
              break;
            case 'paymentTerms':
              this.paymentTermsList = item.masterValue;
              break;
          }
        });
      }
    });
  }
  getCountryList() {
    this.rfqIntakeService.getCountryList().subscribe((res: any) => {
      this.countryList = res.data;
      
      // Find United States in the country list
      const unitedStates = this.countryList.find((country: any) => 
        country.country.toLowerCase() === 'united states' || 
        country.country.toLowerCase() === 'usa' || 
        country.country.toLowerCase() === 'u.s.a.'
      );
      
      // If United States is found, set it as default
      if (unitedStates) {
        this.rfqForm.patchValue({
          country: unitedStates.countryId
        });
      }
    });
  }
  getStateList(countryId: string) {
    this.rfqIntakeService.getStateList(countryId).subscribe((res: any) => {
      this.stateList = res.data;
      // Patch state after stateList is loaded
      if (this.savedState) {
        this.rfqForm.patchValue({ state: this.savedState });
      }
    });
  }

}
